<template>
  <div class="locations">

    <header class="masthead">
      <h1 class="title">Offices<span class="dot">.</span></h1>
      <p class="sub">
        The buildings Vero knows about. Doors are assigned to these, and the
        colour is the badge shown on the display when somebody is here.
      </p>
    </header>

    <!-- Add -->
    <div class="form">
      <label class="field short">
        <span class="field-label">Country</span>
        <input
          v-model="form.country"
          class="input"
          type="text"
          maxlength="2"
          placeholder="DK"
          list="known-countries"
        >
        <!-- Suggestions only. A location can be created in a country before
             anybody in Entra is assigned to it. -->
        <datalist id="known-countries">
          <option v-for="c in knownCountries" :key="c.code" :value="c.code">{{ c.name }}</option>
        </datalist>
      </label>

      <label class="field grow">
        <span class="field-label">Name</span>
        <input v-model="form.name" class="input" type="text" placeholder="Birkerod">
      </label>

      <div class="field">
        <span class="field-label">Colour</span>
        <!-- The same swatches as the table below. A dropdown of hex strings
             asked you to picture a colour from its number. -->
        <div class="swatches picker">
          <button
            v-for="c in PALETTE"
            :key="c"
            class="swatch"
            :class="{ on: form.colour === c }"
            :style="{ background: c }"
            :aria-label="c"
            type="button"
            @click="form.colour = c"
          ></button>
        </div>
      </div>

      <button class="btn primary" :disabled="!canAdd || adding" @click="add">
        {{ adding ? 'Adding…' : 'Add location' }}
      </button>
    </div>

    <p v-if="formError" class="msg err">{{ formError }}</p>

    <!-- List -->
    <p v-if="loading" class="msg">Reading locations</p>

    <p v-else-if="error" class="msg err">
      {{ error }}
      <button class="link" @click="load">Try again</button>
    </p>

    <p v-else-if="locations.length === 0" class="msg">
      No offices yet. Add one above, then assign doors to it under Doors.
    </p>

    <table v-else class="table">
      <thead>
        <tr>
          <th class="col-country">Country</th>
          <th class="col-name">Name</th>
          <th class="col-colour">Colour</th>
          <th class="col-actions"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="loc in locations" :key="`${loc.country}|${loc.name}`">
          <td><span class="chip">{{ loc.country }}</span></td>

          <td>
            <!-- Renamed on blur, like the badge page. A rename moves the
                 location's doors with it, which is why it goes through its own
                 endpoint rather than a delete and an add. -->
            <input
              class="name-input"
              type="text"
              :value="draft[key(loc)] ?? loc.name"
              @input="draft[key(loc)] = $event.target.value"
              @blur="commitRename(loc)"
              @keyup.enter="$event.target.blur()"
            >
          </td>

          <td>
            <div class="swatches">
              <button
                v-for="c in PALETTE"
                :key="c"
                class="swatch"
                :class="{ on: loc.colour === c }"
                :style="{ background: c }"
                :aria-label="c"
                @click="setColour(loc, c)"
              ></button>
            </div>
          </td>

          <td class="actions">
            <span v-if="state[key(loc)]" class="state">{{ state[key(loc)] }}</span>
            <button class="link danger" @click="remove(loc)">Delete</button>
          </td>
        </tr>
      </tbody>
    </table>

  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue'
import { getSiteHierarchy } from '@/services/GraphService'
import {
  PALETTE, getLocations, addLocation, renameLocation,
  setOfficeColour, clearOfficeColour
} from '@/services/OfficeColourService'

const locations = ref([])
const knownCountries = ref([])
const draft = ref({})
const state = ref({})

const loading = ref(true)
const error = ref(null)
const formError = ref(null)
const adding = ref(false)

const form = ref({ country: '', name: '', colour: PALETTE[0] })

const canAdd = computed(() =>
  form.value.country.trim().length === 2 && form.value.name.trim().length > 0
)

function key(loc) {
  return `${loc.country}|${loc.name}`
}

function flash(k, value) {
  state.value = { ...state.value, [k]: value }
  setTimeout(() => {
    const next = { ...state.value }
    if (next[k] === value) delete next[k]
    state.value = next
  }, 1500)
}

async function load() {
  loading.value = true
  error.value = null

  try {
    locations.value = await getLocations()

    // Only to populate the country suggestions. Not required: a location can
    // exist before anyone in Entra is assigned to that country.
    const hierarchy = await getSiteHierarchy().catch(() => null)
    knownCountries.value = hierarchy?.countries ?? []

    draft.value = {}
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  } finally {
    loading.value = false
  }
}

async function add() {
  formError.value = null

  const country = form.value.country.trim().toUpperCase()
  const name = form.value.name.trim()

  if (locations.value.some((l) => l.country === country && l.name === name)) {
    formError.value = 'That location already exists'
    return
  }

  adding.value = true

  try {
    await addLocation(country, name, form.value.colour)
    form.value = { country: '', name: '', colour: PALETTE[0] }
    await load()
  } catch (err) {
    console.error(err)
    formError.value = err.message ?? String(err)
  } finally {
    adding.value = false
  }
}

async function commitRename(loc) {
  const k = key(loc)
  const typed = (draft.value[k] ?? loc.name).trim()

  if (typed === loc.name || typed === '') {
    draft.value = { ...draft.value, [k]: loc.name }
    return
  }

  try {
    await renameLocation(loc.country, loc.name, typed)
    await load()
  } catch (err) {
    console.error(err)
    flash(k, err.message ?? 'failed')
    draft.value = { ...draft.value, [k]: loc.name }
  }
}

async function setColour(loc, colour) {
  const k = key(loc)

  // Applied immediately then saved; waiting on a round trip makes a colour
  // swatch feel broken.
  loc.colour = colour

  try {
    await setOfficeColour(loc.country, loc.name, colour)
    flash(k, 'saved')
  } catch (err) {
    console.error(err)
    flash(k, err.message ?? 'failed')
    await load()
  }
}

async function remove(loc) {
  // Deleting a location leaves any door assigned to it pointing nowhere, and
  // those events would stop resolving. Worth a pause rather than a click.
  const ok = window.confirm(
    `Delete ${loc.name}? Any door assigned to it will need reassigning.`
  )
  if (!ok) return

  try {
    await clearOfficeColour(loc.country, loc.name)
    await load()
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  }
}

onMounted(load)
</script>


<style scoped>
.locations {
  --ink: #003c6c;
  --ink-soft: #64748b;
  --accent: #ed6e01;
  --line: #dfe4e9;

  max-width: 1000px;
  margin: 0;
  padding: 0 24px 80px;
  text-align: left;
  color: #191c1e;
}

.masthead {
  padding: 30px 0 22px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 24px;
}

.title {
  font-size: clamp(28px, 3.8vw, 40px);
  font-weight: 700;
  letter-spacing: -0.03em;
  margin: 0;
  color: var(--ink);
}

.dot { color: var(--accent); }

.sub {
  font-size: 14.5px;
  line-height: 1.55;
  color: var(--ink-soft);
  margin: 8px 0 0;
  max-width: 62ch;
}

/* ---------- Add form ---------- */

.form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 14px;
  margin-bottom: 24px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field.grow { flex: 1; min-width: 220px; }
.field.short { width: 110px; }

.field-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.input {
  font-family: inherit;
  font-size: 15px;
  height: 46px;
  padding: 0 14px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  color: #191c1e;
  outline: none;
  box-sizing: border-box;
  width: 100%;
}

.input:focus { border-color: var(--ink); }

.btn {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  height: 46px;
  padding: 0 22px;
  border: 1px solid var(--ink);
  border-radius: 10px;
  background: transparent;
  color: var(--ink);
  cursor: pointer;
}

.btn:disabled { opacity: 0.4; cursor: default; }
.btn.primary { background: var(--ink); color: #fff; }
.btn.primary:hover:not(:disabled) { background: #00548f; }

/* ---------- Table ---------- */

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
}

.table th {
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-soft);
  padding: 0 12px 10px 0;
  border-bottom: 1px solid var(--line);
}

.table td {
  padding: 9px 12px 9px 0;
  border-bottom: 1px solid #f1f4f7;
  vertical-align: middle;
}

.col-country { width: 12%; }
.col-name { width: 38%; }
.col-colour { width: 30%; }
.col-actions { width: 20%; }

.chip {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  background: var(--ink);
  color: #fff;
  padding: 4px 9px;
  border-radius: 5px;
}

.name-input {
  font-family: inherit;
  font-size: 15px;
  width: 100%;
  height: 38px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 7px;
  background: transparent;
  outline: none;
  box-sizing: border-box;
  color: var(--ink);
  font-weight: 600;
}

.name-input:hover { border-color: var(--line); }
.name-input:focus { border-color: var(--ink); background: #fff; }

/* ---------- Colour ---------- */

.swatches {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

/* In the add form the swatches stand in for an input, so they line up with the
   fields either side rather than sitting at text height. */
.swatches.picker {
  height: 46px;
  align-items: center;
}

.swatch {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 2px solid transparent;
  cursor: pointer;
  padding: 0;
}

.swatch.on {
  border-color: #191c1e;
  box-shadow: 0 0 0 2px #fff inset;
}

/* ---------- Actions ---------- */

.actions {
  text-align: right;
  white-space: nowrap;
}

.state {
  font-size: 12.5px;
  color: var(--ink-soft);
  margin-right: 12px;
}

/* ---------- Messages ---------- */

.msg {
  font-size: 16px;
  color: var(--ink-soft);
  padding: 8px 0;
  margin: 0;
  max-width: 60ch;
}

.msg.err { color: #b3261e; }

.link {
  font-family: inherit;
  font-size: 14px;
  color: var(--ink);
  background: none;
  border: none;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
}

.link.danger { color: #b3261e; }

@media (max-width: 760px) {
  .locations { padding: 0 20px 60px; }
  .form { flex-direction: column; align-items: stretch; }
  .field.short { width: 100%; }
}
</style>
